import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { ApiGet } from '../../../../ApiServices/ApiServices';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Stats from dashboard summary API
  const [stats, setStats] = useState({
    newOrders: 0,
    preparing: 0,
    ready: 0,
    totalOrders: 0,
    totalRevenue: 0
  });

  // Dashboard summary data
  const [dashboardSummary, setDashboardSummary] = useState(null);

  // Helper function to format date as YYYY-MM-DD
  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch dashboard summary from API
  const fetchDashboardSummary = async () => {
    setLoading(true);
    setError('');

    try {
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 30); // Last 30 days

      const response = await ApiGet('/api/kitchen/dashboard/summary', {
        fromDate: formatDateForApi(fromDate),
        toDate: formatDateForApi(toDate)
      });

      if (response.success) {
        const data = response.success.data.data;
        setDashboardSummary(data);

        // Calculate new orders from ordersByStatus (PENDING + ACCEPTED)
        const ordersByStatus = data.ordersByStatus || {};
        const newOrdersCount = (ordersByStatus.PENDING || 0) +
                              (ordersByStatus.ACCEPTED || 0) +
                              (ordersByStatus.ACCEPTED_ORDER || 0);

        // Calculate preparing orders
        const preparingCount = data.preparingOrders ||
                              (ordersByStatus.PREPARING_ORDER || 0);

        // Calculate ready orders
        const readyCount = data.readyOrders ||
                          (ordersByStatus.READY_FOR_ORDER || 0) +
                          (ordersByStatus.READYFORORDER || 0);

        setStats({
          newOrders: newOrdersCount,
          preparing: preparingCount,
          ready: readyCount,
          totalOrders: data.totalOrders || 0,
          totalRevenue: data.totalRevenue || 0
        });
      } else {
        setError('Failed to load dashboard data');
        toast.error('Failed to load dashboard data');
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
      console.error('Failed to fetch dashboard summary:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardSummary();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDashboardSummary, 30000);
    return () => clearInterval(interval);
  }, []);

  const StatCard = ({ title, value, color, borderColor }) => (
    <Card className="h-100 border-0 shadow-sm" style={{ borderTop: `4px solid ${borderColor}` }}>
      <Card.Body className="text-center py-4">
        <div className="fs-2 fw-bold" style={{ color }}>{value}</div>
        <div className="text-muted text-uppercase small mt-1">{title}</div>
      </Card.Body>
    </Card>
  );

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
          Kitchen Dashboard
        </h3>
        <Button
          variant="primary"
          onClick={fetchDashboardSummary}
          disabled={loading}
          style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
        >
          {loading ? (
            <Spinner animation="border" size="sm" className="me-2" />
          ) : (
            <i className="bi bi-arrow-clockwise me-2"></i>
          )}
          Refresh
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {loading && !dashboardSummary ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Loading dashboard...</div>
        </div>
      ) : (
        <>
          {/* All Stats Cards */}
          <Row className="g-3 mb-4">
            {/* Main Stats */}
            <Col lg={3} md={6}>
              <StatCard title="NEW ORDERS" value={stats.newOrders} color="#f59e0b" borderColor="#f59e0b" />
            </Col>
            <Col lg={3} md={6}>
              <StatCard title="PREPARING" value={stats.preparing} color="#3b82f6" borderColor="#3b82f6" />
            </Col>
            <Col lg={3} md={6}>
              <StatCard title="READY" value={stats.ready} color="#10b981" borderColor="#10b981" />
            </Col>
            <Col lg={3} md={6}>
              <StatCard title="TOTAL ORDERS" value={stats.totalOrders} color="#8b5cf6" borderColor="#8b5cf6" />
            </Col>

            {/* Orders by Status Cards */}
            {dashboardSummary?.ordersByStatus && Object.entries(dashboardSummary.ordersByStatus).map(([status, count]) => {
              const statusColors = {
                'PENDING': { color: '#f59e0b', border: '#f59e0b' },
                'ACCEPTED': { color: '#3b82f6', border: '#3b82f6' },
                'ACCEPTED_ORDER': { color: '#6366f1', border: '#6366f1' },
                'PREPARING_ORDER': { color: '#8b5cf6', border: '#8b5cf6' },
                'READY_FOR_ORDER': { color: '#10b981', border: '#10b981' },
                'READYFORORDER': { color: '#14b8a6', border: '#14b8a6' },
                'COMPLETED': { color: '#22c55e', border: '#22c55e' },
                'CANCELLED': { color: '#ef4444', border: '#ef4444' }
              };
              const colors = statusColors[status] || { color: '#64748b', border: '#64748b' };

              return (
                <Col lg={2} md={4} sm={6} key={status}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderTop: `4px solid ${colors.border}` }}>
                    <Card.Body className="text-center py-4">
                      <div className="fs-2 fw-bold" style={{ color: colors.color }}>{count}</div>
                      <div className="text-muted text-uppercase small mt-1">
                        {status.replace(/_/g, ' ')}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* Branch Info Cards */}
          {dashboardSummary && (
            <Row className="g-3">
              <Col md={4}>
                <Card className="border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <Card.Body className="text-white">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                        <i className="bi bi-building fs-4"></i>
                      </div>
                      <div>
                        <div className="text-uppercase small" style={{ opacity: 0.8 }}>Branch Name</div>
                        <div className="fs-5 fw-bold">{dashboardSummary.branchName || '-'}</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                  <Card.Body className="text-white">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                        <i className="bi bi-calendar-range fs-4"></i>
                      </div>
                      <div>
                        <div className="text-uppercase small" style={{ opacity: 0.8 }}>Date Range</div>
                        <div className="fs-6 fw-bold">{dashboardSummary.fromDate}</div>
                        <div className="fs-6 fw-bold">to {dashboardSummary.toDate}</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                  <Card.Body className="text-white">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                        <i className="bi bi-currency-rupee fs-4"></i>
                      </div>
                      <div>
                        <div className="text-uppercase small" style={{ opacity: 0.8 }}>Total Revenue</div>
                        <div className="fs-4 fw-bold">₹{stats.totalRevenue.toFixed(2)}</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

export default Dashboard;
